<template>
  <div class="category-audit-page">
    <!-- 顶部导航 -->
    <header class="header">
      <div class="header-left" @click="goBack">
        <span class="back-icon">←</span>
        <span class="header-title">{{ $t('admin.CategoryAudit.line7_text') }}</span>
      </div>
      <button class="header-icon" @click="handleLogout">🚪</button>
    </header>

    <!-- 统计卡片 -->
    <div class="stats-row">
      <div class="stat-card">
        <span class="stat-num" style="color: #ff9800;">{{ stats.pending }}</span>
        <span class="stat-label">{{ $t('admin.CategoryAudit.line16_text') }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-num" style="color: #4caf50;">{{ stats.approved }}</span>
        <span class="stat-label">{{ $t('admin.CategoryAudit.line20_text') }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-num" style="color: #f44336;">{{ stats.rejected }}</span>
        <span class="stat-label">{{ $t('admin.CategoryAudit.line24_text') }}</span>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner">⏳</div>
      <div class="loading-text">{{ $t('common.loading') }}</div>
    </div>

    <!-- 待审核列表 -->
    <div v-else class="audit-list">
      <div v-for="item in auditList" :key="item.id" class="audit-card">
        <div class="audit-header">
          <span class="user-info">👤 用户 ID: {{ item.userId }}</span>
          <span class="time-info">🕐 {{ formatTime(item.createdAt) }}</span>
        </div>

        <div class="product-info">
          <div class="product-label">{{ $t('admin.CategoryAudit.line43_text') }}</div>
          <div class="product-name">{{ item.productName }}</div>
        </div>

        <div class="category-suggestion">
          <div class="suggestion-label">{{ $t('admin.CategoryAudit.line48_text') }}</div>
          <div class="suggestion-content">
            <span class="category-icon">{{ item.suggestedIcon || '📦' }}</span>
            <span class="category-name">{{ item.suggestedCategory }}</span>
          </div>
        </div>

        <!-- 审核操作区 - 直接显示 -->
        <div class="audit-form">
          <div class="form-row">
            <label>{{ $t('admin.CategoryAudit.line58_text') }}</label>
            <select v-model="item.selectedCategory" class="form-select">
              <option value="">{{ $t('admin.CategoryAudit.line60_text') }}</option>
              <option v-for="cat in categoryOptions" :key="cat.name" :value="cat.name">
                {{ cat.icon }} {{ cat.name }}
              </option>
              <option value="__new__">{{ $t('admin.CategoryAudit.line64_text') }}</option>
            </select>
          </div>
          
          <!-- 新建分类时显示 -->
          <div v-if="item.selectedCategory === '__new__'" class="new-category-form">
            <div class="form-row">
              <label>{{ $t('admin.CategoryAudit.line71_text') }}</label>
              <input v-model="item.newCategoryName" type="text" class="form-input-inline" :placeholder="$t('admin.CategoryAudit.line72_placeholder')" />
            </div>
            <div class="form-row">
              <label>{{ $t('admin.CategoryAudit.line75_text') }}</label>
              <div class="icon-selector">
                <span 
                  v-for="icon in commonIcons" 
                  :key="icon"
                  :class="['icon-item', item.selectedIcon === icon ? 'selected' : '']"
                  @click="item.selectedIcon = icon"
                >{{ icon }}</span>
              </div>
            </div>
          </div>
          
          <!-- 修改现有分类时显示 -->
          <div v-else-if="item.selectedCategory" class="modify-category-form">
            <div class="form-row">
              <label>{{ $t('admin.CategoryAudit.line90_text') }}</label>
              <div class="icon-selector">
                <span 
                  v-for="icon in commonIcons" 
                  :key="icon"
                  :class="['icon-item', item.selectedIcon === icon ? 'selected' : '']"
                  @click="item.selectedIcon = icon"
                >{{ icon }}</span>
              </div>
            </div>
          </div>
          
          <div class="form-row">
            <label>{{ $t('admin.CategoryAudit.line103_text') }}</label>
            <input v-model="item.rejectReason" type="text" class="form-input-inline" :placeholder="$t('admin.CategoryAudit.line104_placeholder')" />
          </div>
          
          <div class="audit-buttons">
            <button class="btn-approve" @click="approve(item)">{{ $t('admin.CategoryAudit.line108_text') }}</button>
            <button class="btn-reject" @click="reject(item)">{{ $t('admin.CategoryAudit.line109_text') }}</button>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="auditList.length === 0" class="empty-state">
        <div class="empty-icon">✅</div>
        <div class="empty-text">{{ $t('admin.CategoryAudit.line117_text') }}</div>
        <div class="empty-hint">{{ $t('admin.CategoryAudit.line118_text') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { logout } from '@/utils/auth';
import axios from 'axios';
import AppConfig from '@/config';

const api = axios.create({
  baseURL: AppConfig.backendUrl + '/api',
  timeout: 30000
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default {
  name: 'CategoryAudit',
  data() {
    return {
      loading: false,
      auditList: [],
      stats: { pending: 0, approved: 0, rejected: 0 },
      categoryOptions: [],
      commonIcons: ['📦', '🥩', '🐟', '🥬', '🍎', '🍚', '🧂', '🥛', '🍞', '🧊', '🍗', '🥚', '🦐', '🦀', '🥕', '🌽', '🍄', '🥜', '🍯', '🧄']
    };
  },
  computed: {
    ...mapGetters(['userName'])
  },
  async mounted() {
    await this.loadPendingList();
    await this.loadStats();
    await this.loadCategoryOptions();
  },
  methods: {
    async handleLogout() { await logout(); },

    async loadPendingList() {
      this.loading = true;
      try {
        const res = await api.get('/admin/category-audit/pending');
        this.auditList = (res.data?.data?.content || []).map(item => ({
          ...item,
          selectedCategory: '',
          selectedIcon: item.suggestedIcon || '📦',
          newCategoryName: '',
          rejectReason: ''
        }));
      } catch (error) {
        this.showToast('加载失败', 'error');
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      try {
        const res = await api.get('/admin/category-audit/stats');
        this.stats = res.data?.data || { pending: 0, approved: 0, rejected: 0 };
      } catch (error) { console.error(error); }
    },

    async loadCategoryOptions() {
      try {
        const res = await api.get('/admin/category-rules');
        this.categoryOptions = (res.data?.data || []).map(r => ({
          name: r.category,
          icon: r.icon || '📦'
        }));
      } catch (error) { console.error(error); }
    },

    async approve(item) {
      let category = item.selectedCategory;
      let icon = item.selectedIcon || '📦';
      
      // 新建分类验证
      if (category === '__new__') {
        if (!item.newCategoryName?.trim()) {
          this.showToast('请输入新分类名称', 'error');
          return;
        }
        category = item.newCategoryName.trim();
        // 新建分类必须选择图标
        if (!item.selectedIcon) {
          this.showToast('请选择分类图标', 'error');
          return;
        }
      }
      
      if (!category) {
        this.showToast('请选择分类', 'error');
        return;
      }

      try {
        const res = await api.post(`/admin/category-audit/${item.id}/approve`, {
          approvedCategory: category,
          approvedIcon: icon,
          auditorId: 1
        });
        
        // 显示处理结果
        const result = res.data?.data;
        let message = '✅ 审核通过！';
        if (result) {
          if (result.actionType === 'modify') {
            message = `✅ 审核通过！已将 "${result.suggestedCategory}" 修改为 "${result.approvedCategory}"，并更新了 ${result.updatedProductCount || 0} 个商品`;
          } else if (result.actionType === 'new') {
            message = `✅ 审核通过！已新增分类 "${result.approvedCategory}" (${icon})，并更新了 ${result.updatedProductCount || 0} 个商品`;
          }
        }
        this.showToast(message, 'success');
        await this.refresh();
      } catch (error) {
        this.showToast('操作失败：' + (error.response?.data?.message || error.message), 'error');
      }
    },

    async reject(item) {
      if (!item.rejectReason?.trim()) {
        this.showToast('请输入拒绝原因', 'error');
        return;
      }
      try {
        await api.post(`/admin/category-audit/${item.id}/reject`, {
          rejectReason: item.rejectReason.trim(),
          auditorId: 1
        });
        this.showToast('已拒绝', 'success');
        await this.refresh();
      } catch (error) {
        this.showToast('操作失败', 'error');
      }
    },

    async refresh() {
      await this.loadPendingList();
      await this.loadStats();
      await this.loadCategoryOptions();
    },

    formatTime(timeStr) {
      if (!timeStr) return '';
      const date = new Date(timeStr);
      const diff = Date.now() - date;
      if (diff < 60000) return '刚刚';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
      return `${date.getMonth() + 1}月${date.getDate()}日`;
    },

    goBack() { this.$router.back(); },

    showToast(message, type) {
      const toast = document.createElement('div');
      toast.textContent = message;
      toast.style.cssText = `position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:${type==='error'?'rgba(255,77,79,0.9)':'rgba(52,199,89,0.9)'};color:#fff;padding:12px 24px;border-radius:8px;font-size:14px;z-index:99999`;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2000);
    }
  }
};
</script>

<style scoped>
.category-audit-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 16px 16px 40px;
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: -16px -16px 16px;
}

.header-left { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.back-icon { font-size: 20px; }
.header-title { font-size: 18px; font-weight: 700; }
.header-icon {
  width: 36px; height: 36px; display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.2); border: none; font-size: 18px; cursor: pointer; border-radius: 50%;
}

.stats-row {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 16px;
}

.stat-card {
  background: #fff; padding: 16px; border-radius: 12px; text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1); display: flex; flex-direction: column; gap: 4px;
}

.stat-num { font-size: 24px; font-weight: 700; }
.stat-label { font-size: 12px; color: #666; }

.loading-state { text-align: center; padding: 60px 16px; }
.loading-spinner { font-size: 48px; margin-bottom: 16px; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.loading-text { font-size: 16px; color: #999; }

.audit-list { display: flex; flex-direction: column; gap: 16px; }

.audit-card {
  background: #fff; border-radius: 12px; padding: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.audit-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 12px; font-size: 12px; color: #999;
}

.product-info { margin-bottom: 12px; }
.product-label { font-size: 12px; color: #666; margin-bottom: 4px; }
.product-name { font-size: 16px; font-weight: 600; color: #333; }

.category-suggestion {
  background: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;
}

.suggestion-label { font-size: 12px; color: #666; margin-bottom: 6px; }
.suggestion-content { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.category-icon { font-size: 20px; }
.category-name { font-size: 15px; font-weight: 500; color: #333; }

.audit-type-badge {
  font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 500;
}
.audit-type-badge.modify { background: #fff3e0; color: #ff9800; }
.audit-type-badge.new { background: #e8f5e9; color: #4caf50; }

/* 审核表单区 */
.audit-form {
  border-top: 1px solid #f0f0f0; padding-top: 16px;
}

.new-category-form, .modify-category-form {
  background: #f9f9f9; padding: 12px; border-radius: 8px; margin-bottom: 12px;
}

.form-row {
  display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-row label {
  font-size: 13px; color: #666; min-width: 70px;
}

.form-select, .form-input-inline {
  flex: 1; padding: 8px 12px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px;
}

.form-select:focus, .form-input-inline:focus {
  outline: none; border-color: #667eea;
}

.icon-selector {
  display: flex; flex-wrap: wrap; gap: 6px; flex: 1;
}

.icon-item {
  font-size: 20px; padding: 6px; border-radius: 6px; cursor: pointer;
  background: #f5f5f5; transition: all 0.2s;
}

.icon-item:hover { background: #e3f2fd; transform: scale(1.1); }
.icon-item.selected { background: #e3f2fd; border: 2px solid #2196f3; }

.audit-buttons {
  display: flex; gap: 12px; margin-top: 16px;
}

.btn-approve, .btn-reject {
  flex: 1; padding: 10px; border: none; border-radius: 6px;
  font-size: 14px; font-weight: 500; cursor: pointer;
}

.btn-approve { background: #e8f5e9; color: #4caf50; }
.btn-approve:hover { background: #c8e6c9; }

.btn-reject { background: #ffebee; color: #f44336; }
.btn-reject:hover { background: #ffcdd2; }

.empty-state { text-align: center; padding: 60px 16px; }
.empty-icon { font-size: 64px; margin-bottom: 16px; }
.empty-text { font-size: 16px; color: #666; margin-bottom: 8px; }
.empty-hint { font-size: 13px; color: #999; }
</style>
